/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { CartCookie } from '@utils'
import { View, Pressable, Icon, Image, Avatar } from '@components/atoms'
import Badge from '@mui/material/Badge'
// 좌상단 로고는 기존 HomeNav 와 동일하게 라이언 심볼 + 워딩 이미지 (2026-09-08 사용자 요청)
import logoPng from '@public/images/logo/logo_black.png'
import logoWordingPng from '@public/images/logo/logo_wording.png'

import { useAppSelector, useAppDispatch, configActions } from '@redux/index'
import { useNavigate, useLocation } from 'react-router-dom'
import { HomeNavMenuType } from '@redux/types'
import { CustomerType } from '@schemas'
import { isExternalUri, FORUM_URL, forumEntryUrl } from '@configs'
import { mono, monoFont, monoGutter, monoMaxWidth } from './monolith.tokens'

interface PropsType {
	menuList: HomeNavMenuType[]
}

/** 네비 높이. HomeLayout 의 스페이서가 같은 값을 쓴다. */
export const MONOLITH_NAV_HEIGHT = 72

/**
 * Monolith 상단 네비.
 *
 * GNB (2026-09-09, SITEMAP_PLAN §9): Products · Shop · Support · Forum · About · Contact | Login · Cart.
 * 메뉴는 config.homeMenu() 를 그대로 그린다. 예전의 "PLS 8" 끼워넣기는 Products 가 생기면서 뺐다.
 * Forum 만 사이트 밖(forum.judahs.io)이라 `navigate()` 가 아니라 주소창을 바꾼다 — 같은 탭이다.
 * 카트 배지·로그인/계정·모바일 드로어 토글은 기존 HomeNav 와 같은 동작을 유지한다.
 */
export const MonolithNav = (props: PropsType): JSX.Element => {
	// hook
	const navigate = useNavigate()
	const location = useLocation()

	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const isHomeOpenDrawer: boolean = useAppSelector(state => state.config.isHomeOpenDrawer)
	const isLogedin: boolean = useAppSelector(state => state.customerAuth.isLogedin)
	const customer: CustomerType = useAppSelector(state => state.customerAuth.customer)

	// state
	const [cartLength, setCartLength] = React.useState<number>(0)

	React.useEffect(() => {
		handleStorage()

		window.addEventListener('storage', handleStorage)
		return () => window.removeEventListener('storage', handleStorage)
	}, [])

	const handleStorage = () => {
		const localeCartSrt = localStorage.getItem('cart')
		const localeCartList =
			localeCartSrt === null || localeCartSrt === '' ? [] : JSON.parse(localeCartSrt)
		const cartItemLength =
			!Array.isArray(localeCartList) || localeCartList.length === 0
				? 0
				: localeCartList.length

		setCartLength(cartItemLength)
		// 포럼 헤더 Cart 배지가 읽는다 (utils/CartCookie)
		CartCookie.sync(cartItemLength)
	}

	const onMenuTogglePress = () => {
		reduxDispatch(configActions.IS_HOME_OPEN_DRAWER(!isHomeOpenDrawer))
	}

	// 메뉴 라벨은 목업대로 첫 글자만 대문자.
	const navItems: { id: string; title: string; uri: string }[] = React.useMemo(() => {
		const toTitle = (s: string) =>
			s.length === 0 ? s : s[0].toUpperCase() + s.slice(1).toLowerCase()

		return props.menuList.map(aMenu => ({
			id: aMenu.id,
			title: toTitle(aMenu.title),
			uri: aMenu.uri,
		}))
	}, [props.menuList])

	const isActive = (uri: string): boolean => {
		// 사이트 밖 항목(Forum)은 우리 경로와 겹칠 일이 없어 켜지지 않는다.
		if (isExternalUri(uri)) return false
		if (uri === '/') {
			return location.pathname === '/'
		}
		// 소개 상세(/products/{code})는 Products 활성, 구매 상세(/shop/{code})는 Shop 활성.
		return location.pathname === uri || location.pathname.startsWith(`${uri}/`)
	}

	// 계정 4페이지(/customer/*)에 있으면 아바타에 링을 켠다 — 다른 메뉴의 밑줄과 같은 역할.
	const isAccountActive: boolean = location.pathname.startsWith('/customer/')

	const onMenuPress = (uri: string) => {
		if (isExternalUri(uri)) {
			// 포럼은 로그인돼 있으면 로그인된 채로 착지한다 (FORUM_PLAN §11)
			window.location.href = uri === FORUM_URL ? forumEntryUrl() : uri
			return
		}
		navigate(uri)
	}

	return (
		<View css={style} className="monolithNav">
			<View className="mnNavInner">
				<Pressable
					className="mnLogoButton"
					onClick={() => navigate('/')}
					aria-label="Judahs home"
				>
					<Image src={logoPng} className="mnLogoSymbol" />
					<Image src={logoWordingPng} className="mnLogoWording" />
				</Pressable>

				<View className="mnMenu">
					{navItems.map(anItem => (
						<Pressable
							key={anItem.id}
							className={`mnMenuItem ${isActive(anItem.uri) ? 'on' : ''}`}
							onClick={() => onMenuPress(anItem.uri)}
						>
							{anItem.title}
						</Pressable>
					))}

					<span className="mnDivider">|</span>

					{isLogedin ? (
						<Pressable
							className={`mnMenuItem mnIconItem mnAccountItem ${
								isAccountActive ? 'on' : ''
							}`}
							onClick={() => navigate('/customer/info')}
							aria-label={
								customer.name === '' ? 'My account' : `My account — ${customer.name}`
							}
						>
							<Avatar
								picture={customer.profileImageUrl}
								name={customer.name}
								color={customer.profileColor === '' ? mono.ink : customer.profileColor}
								size={28}
								extpandingDisable={true}
							/>
						</Pressable>
					) : (
						<Pressable className="mnMenuItem" onClick={() => navigate('/customer/login')}>
							Login
						</Pressable>
					)}

					<Pressable
						className="mnMenuItem mnIconItem"
						onClick={() => navigate('/cart')}
						aria-label="Cart"
					>
						<Badge badgeContent={cartLength} color="default" className="mnBadge">
							<Icon name="cart-outline" css={{ fontSize: 22 }} />
						</Badge>
					</Pressable>
				</View>

				<View className="mnMobile">
					<Pressable
						className="mnMenuItem mnIconItem"
						onClick={() => navigate('/cart')}
						aria-label="Cart"
					>
						<Badge badgeContent={cartLength} color="default" className="mnBadge">
							<Icon name="cart-outline" css={{ fontSize: 22 }} />
						</Badge>
					</Pressable>
					<Pressable
						className="mnMenuItem mnIconItem"
						onClick={onMenuTogglePress}
						aria-label="Menu"
					>
						<Icon name="menu" css={{ fontSize: 28 }} />
					</Pressable>
				</View>
			</View>
		</View>
	)
}

const style = css({
	position: 'fixed',
	top: 0,
	left: 0,
	right: 0,
	zIndex: 5,
	height: MONOLITH_NAV_HEIGHT,
	background: mono.bg,
	borderBottom: `1px solid ${mono.line}`,
	fontFamily: monoFont.sans,

	'.mnNavInner': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
		maxWidth: monoMaxWidth,
		height: '100%',
		margin: '0 auto',
		padding: `0 ${monoGutter}`,
	},

	'.mnLogoButton': {
		display: 'flex',
		alignItems: 'center',
		padding: 0,
		height: '100%',

		'.mnLogoSymbol': {
			height: 36,
			width: 'auto',
			display: 'block',
		},
		'.mnLogoWording': {
			width: '6.5rem',
			height: 'auto',
			marginLeft: '0.8rem',
			display: 'block',
		},
	},

	'.mnMenu': {
		display: 'flex',
		alignItems: 'center',
		gap: 40,
	},

	'.mnMenuItem': {
		padding: 0,
		fontFamily: monoFont.sans,
		fontSize: 13,
		fontWeight: 500,
		color: mono.faint,
		lineHeight: 1,
		borderBottom: '1px solid transparent',
		paddingBottom: 3,
		marginBottom: -3,

		'&:hover': {
			color: mono.ink,
		},

		'&.on': {
			color: mono.ink,
			borderBottomColor: mono.ink,
		},

		'&.mnIconItem': {
			borderBottom: 0,
			paddingBottom: 0,
			marginBottom: 0,
			display: 'flex',
			alignItems: 'center',
		},
	},

	/** 계정 = 본인 아바타. 색은 안 쓰고(§2) 잉크 원 + 이니셜이다 */
	'.mnAccountItem': {
		'.avatarFace': {
			transition: 'opacity 200ms ease-out',
		},
		'&:hover .avatarFace': {
			opacity: 0.72,
		},
		/** 활성 표시는 다른 항목의 밑줄 대신 헤어라인 링 하나 */
		'&.on .avatarFace': {
			boxShadow: `0 0 0 2px ${mono.bg}, 0 0 0 3px ${mono.ink}`,
		},
	},

	'.mnDivider': {
		color: mono.lineSoft,
		fontSize: 13,
		lineHeight: 1,
	},

	'.mnBadge .MuiBadge-badge': {
		background: mono.ink,
		color: '#fff',
		fontSize: 10,
		minWidth: 16,
		height: 16,
	},

	'.mnMobile': {
		display: 'none',
		alignItems: 'center',
		gap: 20,
	},

	'@media (prefers-reduced-motion: reduce)': {
		'.mnAccountItem .avatarFace': { transition: 'none' },
	},

	'@media (max-width: 890px)': {
		'.mnMenu': {
			display: 'none',
		},
		'.mnMobile': {
			display: 'flex',
		},
	},
})
